import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { EventData, Mark, Participant } from '../types'
import { getEvent, getBookings, addParticipant, updateParticipant, deleteParticipant, type BookingMap } from '../api'
import {
  getCookieId,
  addVisited,
  setMyParticipant,
  getMyParticipants,
  removeMyParticipant,
} from '../lib/identity'
import { MARK_LABEL } from '../lib/marks'
import { card, input, btnPrimary } from '../lib/ui'
import Grid from '../components/Grid'
import ShareBar from '../components/ShareBar'
import MarkPicker from '../components/MarkPicker'

export default function EventView() {
  const { id = '' } = useParams()
  const [data, setData] = useState<EventData | null>(null)
  const [bookings, setBookings] = useState<BookingMap>({})
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [composing, setComposing] = useState(false)
  const [name, setName] = useState('')
  const [comment, setComment] = useState('')
  const [marks, setMarks] = useState<Record<string, Mark>>({})
  const [saving, setSaving] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  const goToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const load = useCallback(async () => {
    try {
      const d = await getEvent(id)
      setData(d)
      addVisited(d.event.id, d.event.title)
      // この端末で未回答ならフォームを開く。回答済みなら畳む。
      const myPid = getMyParticipants()[id]
      const answered = !!myPid && d.participants.some((p) => p.id === myPid)
      setComposing(!answered)
      // 横断ブッキング確度（ベストエフォート）
      getBookings(id).then(setBookings).catch(() => {})
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました')
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setName('')
    setComment('')
    setMarks({})
  }

  const composeNew = () => {
    setEditingId(null)
    resetForm()
    setComposing(true)
  }

  const startEdit = (p: Participant) => {
    if (!data) return
    const m: Record<string, Mark> = {}
    for (const r of data.responses) {
      if (r.participant_id === p.id) m[r.candidate_id] = r.mark
    }
    setEditingId(p.id)
    setName(p.name)
    setComment(p.comment ?? '')
    setMarks(m)
    setComposing(false)
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
  }

  const save = async () => {
    if (!data || !name.trim()) {
      setError('お名前を入れてください')
      return
    }
    setError('')
    setSaving(true)
    try {
      if (editingId) {
        await updateParticipant(editingId, { name: name.trim(), comment, marks })
      } else {
        const { id: newPid } = await addParticipant(data.event.id, {
          name: name.trim(),
          comment,
          cookie_id: getCookieId(),
          marks,
        })
        setMyParticipant(data.event.id, newPid)
      }
      resetForm()
      setEditingId(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (p: Participant) => {
    if (!confirm(`${p.name} さんの回答を削除しますか？`)) return
    await deleteParticipant(p.id)
    if (editingId === p.id) setEditingId(null)
    await load()
  }

  // この回答行を「自分」に紐付ける（1イベント=1自分：前の紐付けは外す）
  const claim = async (pid: string) => {
    const prev = getMyParticipants()[id]
    if (prev && prev !== pid) await updateParticipant(prev, { cookie_id: '' })
    await updateParticipant(pid, { cookie_id: getCookieId() })
    setMyParticipant(id, pid)
    await load()
  }

  // 紐付けだけ外す（回答行は消さない）
  const unclaim = async (pid: string) => {
    await updateParticipant(pid, { cookie_id: '' })
    removeMyParticipant(id)
    await load()
  }

  if (error && !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-medium text-conflict">{error}</p>
        <a href="/" className="mt-4 inline-block text-sm font-medium text-brand hover:underline">
          トップへ戻る
        </a>
      </div>
    )
  }

  if (!data) {
    return <div className="px-4 py-16 text-center text-muted">読み込み中…</div>
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : ''
  const myPid = getMyParticipants()[id]
  const myParticipant = data.participants.find((p) => p.id === myPid) ?? null
  const showForm = editingId !== null || composing
  const sectionLabel = 'mb-1.5 flex items-center gap-2 text-sm font-semibold text-muted'
  const bar = <span className="inline-block h-3.5 w-1 rounded-full bg-brand" />

  return (
    <>
      <div className="animate-in mx-auto max-w-4xl px-4 pb-28 pt-5 sm:pb-16">
      <div className="mb-3 flex items-center justify-between text-sm">
        <a href="/" className="font-medium text-muted transition hover:text-brand">
          ← ヨテピース
        </a>
        <a href="/me" className="font-medium text-brand transition hover:text-brand-ink">
          マイ予定
        </a>
      </div>

      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink">
            {data.event.title}
          </h1>
          {data.event.description && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted">{data.event.description}</p>
          )}
        </div>
        <a
          href={`/e/${id}/edit`}
          className="mt-1 shrink-0 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-medium text-muted transition hover:border-brand/40 hover:text-brand-ink"
        >
          ⚙ 編集
        </a>
      </div>

      <div className="mb-6">
        <ShareBar url={shareUrl} />
      </div>

      <h2 className={sectionLabel}>{bar} 日程候補</h2>
      <p className="mb-2.5 text-xs text-muted">名前をタップすると、その人の出欠を編集できます。</p>

      <Grid
        candidates={data.candidates}
        participants={data.participants}
        responses={data.responses}
        bookings={bookings}
        onEdit={startEdit}
        onDelete={remove}
      />
      <p className="mt-2 text-xs text-muted">
        色付きの○ ＝ 逃げ道が少なく参加が分かれて困りそうな印（濃いほど要注意・候補の多くに○なら色は付きません）。○のそばの％ ＝ その人が他の予定に流れる可能性。
      </p>

      <div ref={formRef} className="scroll-mt-4" />

      {/* 回答済みでフォームを畳んでいるとき */}
      {!showForm && myParticipant && (
        <div className={`mt-8 flex flex-wrap items-center justify-between gap-3 p-5 ${card}`}>
          <p className="text-sm text-ink">
            <span className="font-semibold">{myParticipant.name}</span>{' '}
            さんとして回答済みです。
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => startEdit(myParticipant)}
              className={`${btnPrimary} px-5 py-2`}
            >
              編集する
            </button>
            <button
              onClick={composeNew}
              className="rounded-xl border border-line bg-white px-4 py-2 text-sm font-medium text-muted transition hover:border-brand/40 hover:text-brand-ink"
            >
              別の人として回答
            </button>
            <button
              onClick={() => unclaim(myParticipant.id)}
              className="px-2 text-sm font-medium text-muted transition hover:text-ink"
            >
              紐付けを外す
            </button>
          </div>
        </div>
      )}

      {/* 入力フォーム（未回答 or 編集中だけ表示）*/}
      {showForm && (
        <div className={`mt-8 p-5 ${card}`}>
          <div className="mb-4 flex items-center justify-between border-b border-line pb-3">
            <h2 className="font-display text-lg font-bold text-ink">
              {editingId ? '出欠を編集' : '出欠を入力'}
            </h2>
            {myParticipant && (
              <button
                onClick={() => {
                  setEditingId(null)
                  resetForm()
                  setComposing(false)
                }}
                className="text-xs font-medium text-muted hover:text-ink"
              >
                とじる
              </button>
            )}
          </div>

          <label className={sectionLabel}>{bar} 名前</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="お名前"
            className={`${input} mb-5 sm:w-72`}
          />

          <div className="mb-5">
            <label className={sectionLabel}>{bar} 日程候補</label>
            <div className="divide-y divide-line">
              {data.candidates.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="text-sm text-ink">{c.label}</span>
                  <MarkPicker
                    value={marks[c.id]}
                    onChange={(m) => setMarks((prev) => ({ ...prev, [c.id]: m }))}
                  />
                </div>
              ))}
            </div>
          </div>

          <label className={sectionLabel}>{bar} コメント</label>
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="任意"
            className={`${input} mb-5`}
          />

          {error && (
            <p className="mb-3 rounded-xl bg-conflict-soft px-4 py-2.5 text-sm font-medium text-conflict">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={save} disabled={saving} className={`${btnPrimary} px-8 py-3 text-base`}>
              {saving ? '保存中…' : editingId ? '更新する' : '入力する'}
            </button>
            {editingId &&
              (editingId === myPid ? (
                <button
                  onClick={() => unclaim(editingId)}
                  className="rounded-xl border border-line bg-white px-4 py-2.5 text-sm font-medium text-muted transition hover:border-brand/40 hover:text-brand-ink"
                >
                  自分の紐付けを外す
                </button>
              ) : (
                <button
                  onClick={() => claim(editingId)}
                  className="rounded-xl border border-brand/30 bg-brand-soft px-4 py-2.5 text-sm font-semibold text-brand-ink transition hover:border-brand/50"
                >
                  この回答を自分にする
                </button>
              ))}
            <span className="text-xs text-muted">
              <span className="text-ok">{MARK_LABEL.ok}</span> 参加 ·{' '}
              <span className="text-maybe">{MARK_LABEL.maybe}</span> 未定 ·{' '}
              <span className="text-no">{MARK_LABEL.no}</span> 不可
            </span>
          </div>
        </div>
      )}
      </div>

      {/* モバイル固定アクションバー（親指ゾーンで操作）。animate-in(transform)の外に置く */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 px-4 pt-3 shadow-[0_-6px_24px_-12px_rgba(28,31,51,0.25)] backdrop-blur sm:hidden"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {myParticipant && !showForm ? (
          <div className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-sm text-ink">
              <b className="font-semibold">{myParticipant.name}</b> として回答済み
            </span>
            <button onClick={() => startEdit(myParticipant)} className={`${btnPrimary} shrink-0 px-6 py-2.5`}>
              編集
            </button>
          </div>
        ) : (
          <button onClick={goToForm} className={`${btnPrimary} w-full py-3 text-base`}>
            出欠を入力する
          </button>
        )}
      </div>
    </>
  )
}
